import Constants from "@/resources/Constants";
import * as Entities from "@/resources/Entities";

import {
  filterCriteria as groupFilterCriteria,
  searchColumnHeaders as groupColumnHeaders,
} from "../Group/inputs";

import {
  filterCriteria as supplierFilterCriteria,
  searchColumnHeaders as supplierColumnHeaders,
} from "../Supplier/inputs";

import {
  filterCriteria as brandFilterCriteria,
  searchColumnHeaders as brandColumnHeaders,
} from "../Brand/inputs";

import {
  filterCriteria as productFilterCriteria,
  searchColumnHeaders as productColumnHeaders,
} from "../Product/inputs";

import {
  searchColumnHeaders as msoDiscountRuleColumnHeaders,
  filterCriteria as msoDiscountRuleFilterCriteria,
} from "../MSODiscountRule/inputs";

// Import shared options (to break circular dependency)
import {
  discountTypeOptions,
  discountTypeFilterOptions,
  dayOfWeekOptions,
  sourceTypeOptions,
  effectTypeOptions,
  applyToOptions,
  conditionFieldOptions,
  customerConditionFieldOptions,
  conditionOperatorOptions,
  stackingBehaviorOptions,
  defaultEffect,
  defaultCondition,
  defaultConditionGroup,
  defaultBundleGroup,
} from "./sharedOptions";

// Re-export shared options for backward compatibility
export {
  discountTypeOptions,
  discountTypeFilterOptions,
  dayOfWeekOptions,
  sourceTypeOptions,
  effectTypeOptions,
  applyToOptions,
  conditionFieldOptions,
  customerConditionFieldOptions,
  conditionOperatorOptions,
  stackingBehaviorOptions,
  defaultEffect,
  defaultCondition,
  defaultConditionGroup,
  defaultBundleGroup,
};

// Page Header
export const pageHeader = {
  title: "Discount Rule",
  isEditorView: true,
  cancelPath: "discountRules",
  addPath: "discountRuleEditor",
  buttonLabel: "Save",
};

// Page Header
export const listPageHeader = {
  title: "Discount Rules",
  isEditorView: false,
  buttonLabel: "New",
  routerPath: "discountRuleEditor",
};

export const filterCriteria = [
  {
    label: "Name",
    code: "name",
    numCharsToSearch: 1,
    field: { type: "input", label: "name" },
    isPartial: true,
  },
  {
    label: "Online Store Name",
    code: "onlineStoreName",
    numCharsToSearch: 1,
    field: { type: "input", label: "name" },
    isPartial: true,
  },
  { label: "Id", code: "_id", field: { type: "input", label: "Id" } },
];

export const columnHeaders = [
  {
    id: "open",
    type: "button",
    action: "navigateToEntity",
    position: "sticky",
    iconName: "folder-open",
    ariaLabel: "Open",
  },
  {
    title: "Active",
    id: "isActive",
    type: "checkbox",
  },
  {
    title: "Name",
    id: "name",
    size: "large",
  },
  {
    title: "Online Store Name",
    id: "onlineStoreName",
    size: "large",
  },
  {
    title: "Type",
    id: "discountType",
    type: "select",
    options: discountTypeOptions,
    label: "label",
    size: "medium",
  },
  {
    title: "Start Date",
    id: "startDateTime",
    type: "date-extended",
    size: "medium",
  },
  {
    title: "End Date",
    id: "endDateTime",
    type: "date-extended",
    size: "medium",
  },
  {
    title: "Day of Week",
    id: "daysOfWeek",
    type: "search-multi",
    options: dayOfWeekOptions,
    size: "small",
  },
  {
    title: "Description",
    id: "description",
    size: "large",
  },
  {
    title: "Id",
    id: "_id",
  },
];

export const searchColumnHeaders = [
  {
    id: "select",
    type: "button",
    action: "selectItem",
    position: "sticky",
    iconName: "hand-pointer",
    ariaLabel: "Select",
  },
  {
    title: "Name",
    id: "name",
    size: "large",
  },
  {
    title: "Id",
    id: "_id",
  },
];

// Input definitions for the editor
export const discountRuleInfoInputs = [
  {
    id: "msoDiscountRule",
    type: "search",
    title: "MSO Discount Rule",
    placeholder: "Select MSO Discount Rule",
    label: "name",
    entity: Entities.MSODISCOUNTRULE,
    columnHeaders: msoDiscountRuleColumnHeaders,
    filterCriteria: msoDiscountRuleFilterCriteria,
    filter: {
      isActive: true,
      isDeleted: false
    },
  },
  {
    id: "name",
    placeholder: "Rule Name",
    type: "input",
    title: "Rule Name",
    focus: true,
  },
  {
    id: "onlineStoreName",
    placeholder: "Online Store Display Name",
    type: "input",
    title: "Online Store Name",
    hint: "Optional - shown on online store/kiosk instead of Rule Name",
  },
  {
    id: "description",
    placeholder: "Description for Online Store & Kiosk - Optional, displays as tool-tip",
    type: "textarea",
    title: "Online Store Description",
    class: "grid-row-two-column",
  },
  {
    id: "discountType",
    type: "select",
    title: "Discount Type",
    options: discountTypeOptions,
    label: "label",
  },
  {
    id: "isActive",
    type: "checkbox",
    title: "Active",
  },
  {
    id: "saleType",
    type: "select",
    title: "Sale Type",
    options: Constants.listOfSaleType,
    label: "label",
  },
  {
    id: "code",
    type: "input",
    title: "Coupon Code",
  },
  // Note: sourceType is an array field - handled separately in editor
  // Empty array means "all sources"
];

// Schedule inputs
// NOTE: Combined datetime fields for easier filtering
export const scheduleInputs = [
  {
    id: "startDateTime",
    type: "calendar",
    title: "Start Date & Time",
    showTime: true,
  },
  {
    id: "endDateTime",
    type: "calendar",
    title: "End Date & Time",
    showTime: true,
  },
  // Note: daysOfWeek is an array field - handled separately in editor
  // Empty array means "all days"
];

// Trigger inputs
export const triggerInputs = [
  {
    id: "triggers.minQuantity",
    type: "number",
    title: "Minimum Quantity",
    precision: 0,
  },
  {
    id: "triggers.minSpend",
    type: "money",
    title: "Minimum Spend",
    prefix: "$",
  },
  {
    id: "triggers.minCannabisGrams",
    type: "number",
    title: "Minimum Cannabis Grams",
    precision: 2,
  },
  // customerConditions uses ConditionBuilder - see DiscountRuleEditor.vue
];

// Effect inputs
export const effectInputs = [
  {
    id: "type",
    type: "select",
    title: "Effect Type",
    options: effectTypeOptions,
    label: "label",
  },
  {
    id: "value",
    type: "number",
    title: "Value",
    precision: 2,
  },
  {
    id: "maxQuantity",
    type: "number",
    title: "Max Effect Applications",
    precision: 0,
  },
  {
    id: "applyTo",
    type: "select",
    title: "Apply To",
    options: applyToOptions,
    label: "label",
  },
];

// Stacking inputs
export const stackingInputs = [
  {
    id: "stacking.stackingBehavior",
    type: "select",
    title: "Stacking Behavior",
    options: stackingBehaviorOptions,
    label: "label",
  },
  // Note: stacking.excludeTypes is an array field - handled separately in editor
];

// Multiple applications options for rule-level override
export const multipleApplicationsOptions = [
  { label: "Use Store Default", code: null },
  { label: "Yes - Allow Multiple Applications", code: true },
  { label: "No - Apply Once Per Cart", code: false },
];

// Multiple applications input
export const multipleApplicationsInput = {
  id: "allowMultipleApplications",
  type: "select",
  title: "Allow Multiple Applications",
  options: multipleApplicationsOptions,
  label: "label",
};

// Usage limit inputs
export const usageLimitInputs = [
  {
    id: "usageLimits.maxPerOrder",
    type: "number",
    title: "Max Per Order",
    precision: 0,
  },
  {
    id: "usageLimits.maxPerCustomer",
    type: "number",
    title: "Max Per Customer",
    precision: 0,
  },
  {
    id: "usageLimits.maxPerCustomerPerDay",
    type: "number",
    title: "Max Per Customer Per Day",
    precision: 0,
  },
  {
    id: "usageLimits.maxTotalUses",
    type: "number",
    title: "Max Total Uses",
    precision: 0,
  },
];

// Search inputs for condition targets
export const conditionSearchInputs = {
  group: {
    id: "group",
    placeholder: "Select Group",
    type: "search",
    title: "Group",
    label: "name",
    entity: Entities.GROUP,
    columnHeaders: groupColumnHeaders,
    filterCriteria: groupFilterCriteria,
    isClearable: true,
  },
  supplier: {
    id: "supplier",
    placeholder: "Select Supplier",
    type: "search",
    title: "Supplier",
    label: "name",
    entity: Entities.SUPPLIER,
    columnHeaders: supplierColumnHeaders,
    filterCriteria: supplierFilterCriteria,
    isClearable: true,
  },
  brand: {
    id: "brand",
    placeholder: "Select Brand",
    type: "search",
    title: "Brand",
    label: "name",
    entity: Entities.BRAND,
    columnHeaders: brandColumnHeaders,
    filterCriteria: brandFilterCriteria,
    isClearable: true,
  },
  product: {
    id: "product",
    placeholder: "Select Product",
    type: "search",
    title: "Product",
    label: "name",
    entity: Entities.PRODUCT,
    columnHeaders: productColumnHeaders,
    filterCriteria: productFilterCriteria,
    isClearable: true,
  },
};

// Default discount rule
export const defaultSelectedDiscountRule = {
  _id: "NEW",
  name: "",
  onlineStoreName: "",
  description: "",
  code: "",
  discountType: "instant",
  saleType: "medical-and-recreational",
  isActive: true,
  priority: 0,
  sourceType: ["pos", "online-store", "kiosk"],
  startDateTime: null,
  endDateTime: null,
  daysOfWeek: [],
  schedule: {
    daysOfWeek: [],
    timeWindows: [],
  },
  conditions: {
    type: "AND",
    rules: []
  },
  triggers: {
    minQuantity: 0,
    minSpend: 0,
    minCannabisGrams: 0,
    customerConditions: {
      type: "AND",
      rules: []
    }
  },
  effects: [],
  stacking: {
    stackingBehavior: "default",
    excludeTypes: [],
    excludeRuleIds: [],
    onlyStackWithTypes: []
  },
  allowMultipleApplications: null,
  usageLimits: {
    maxPerOrder: 0,
    maxPerCustomer: 0,
    maxPerCustomerPerDay: 0,
    maxTotalUses: 0
  },
  bundleGroups: [],
  tags: [],
  isArchived: false,
  isDeleted: false,
};

